import React, { useEffect, useState, useContext } from 'react';
import cx from 'classnames';

import { userQuery } from '../../queries';
import { useLazyQuery } from '@apollo/client';

import { Modal } from 'components/Modal';
import { Button } from 'components/Button';
import { Icon } from 'components/Icons';
import { Text } from 'components/Text';
import { InputNoRef } from 'components/Input';
import { StoreContext } from 'store';

import styles from './mobile.module.scss';
import { SingleAccordionItem } from 'components/Accordion/AccordionItem';
import { LoaderContent } from 'components/Loaders';
import { api } from 'api';
import { trackEvent } from 'analytics/amplitude';

interface DesktopProps {
  isOpen: boolean;
  onRequestClose: () => void;
  login?: (user: api.Account) => void;
}

export const Desktop: React.FC<DesktopProps> = ({
  isOpen,
  onRequestClose,
  login = () => undefined,
}) => {
  const { translation: t } = useContext(StoreContext);

  const [userId, setUserId] = useState('');
  const [errorTooltip, setErrorTooltip] = useState({
    message: '',
    isVisible: false,
  });

  const handleRequestClose = () => {
    onRequestClose();
    setUserId('');
    setErrorTooltip({
      message: '',
      isVisible: false,
    });
    localStorage.removeItem('auth_component');
  };

  const [getUser, { error, loading }] = useLazyQuery(userQuery);

  useEffect(() => {
    if (error && error.graphQLErrors) {
      const message = error.graphQLErrors[0]?.message;
      if (message === 'not_found') {
        setErrorTooltip({
          message: t.authModal.errors.userNotFound,
          isVisible: true,
        });
      }
    }
  }, [error]);

  return (
    <Modal isOpen={isOpen} onRequestClose={handleRequestClose}>
      {loading && <LoaderContent />}
      <form
        className={styles.wrapper}
        onSubmit={event => {
          event.preventDefault();
          getUser({ variables: { publicId: userId?.toUpperCase() } }).then(data => {
            const user = data?.data?.account as api.Account;
            const component = localStorage.getItem('auth_component');

            if (!user) {
              trackEvent('authorized', { status: 'failed', component });
              return;
            }
            login(user);
            trackEvent('authorized', {
              status: 'success',
              component,
              userId: user.publicId,
              userName: user.name,
            });
            handleRequestClose();
          });
        }}
      >
        <div className={styles.title}>
          <Text.HeaderM style={{ color: 'var(--light-green-grey)' }}>
            {t.authModal.title}
          </Text.HeaderM>
          <Text.HeaderS style={{ color: 'var(--light-green-grey)' }}>
            {t.authModal.subtitle}
          </Text.HeaderS>
        </div>
        <div className={styles.inputContainerWrapper}>
          <Text.CapsL style={{ color: 'var(--beige)' }} className={styles.formTitle}>
            {'Tacticool Public ID'}
          </Text.CapsL>
          <div className={styles.inputWrapper}>
            <InputNoRef
              autoFocus
              error={errorTooltip.isVisible}
              onChange={event => {
                const value = event.target.value;
                setUserId(value);
                setErrorTooltip({
                  ...errorTooltip,
                  isVisible: false,
                });
                setTimeout(() => {
                  setErrorTooltip({
                    isVisible: false,
                    message: '',
                  });
                }, 400);
              }}
              value={userId}
              type="text"
            />
            <Tooltip text={errorTooltip.message} isVisible={errorTooltip.isVisible} />
          </div>
          <Button
            className={styles.formButton}
            size="xl"
            withTriangle
            Icon={Icon.RightArrow}
            type="submit"
            hasMobileSize
          >
            {t.actions.submit}
          </Button>
        </div>
        <div className={styles.accordionItemWrapper}>
          {isOpen ? (
            <SingleAccordionItem expiredHeight={900} title={t.authModal.ruleTitle}>
              <img
                className={styles.authRuleImage}
                src="/img/common/auth-rule-1.webp"
                alt="Authorisation rule img"
              />
              <Text.ParagraphXl className={styles.authRuleText}>
                {t.authModal.rule1}
              </Text.ParagraphXl>
              <img
                className={cx(styles.authRuleImage, styles.authRuleImage2)}
                src="/img/common/auth-rule-2.webp"
                alt="Authorisation rule img"
              />
              <Text.ParagraphXl className={styles.authRuleText}>
                {t.authModal.rule2}
              </Text.ParagraphXl>
            </SingleAccordionItem>
          ) : null}
        </div>
      </form>
    </Modal>
  );
};

interface TooltipProps {
  text: string;
  isVisible: boolean;
}

export const Tooltip: React.FC<TooltipProps> = ({ text, isVisible }) => {
  return (
    <div
      className={styles.tooltip}
      style={{ opacity: isVisible ? 0.9 : 0, zIndex: 10 }}
    >
      <svg width="8" height="5" viewBox="0 0 8 5" fill="var(--error-red)">
        <path d="M4 0L0 5H8L4 0Z" stroke="none" />
      </svg>
      <Text.ParagraphXl style={{ color: 'var(--beige)' }}>{text}</Text.ParagraphXl>
    </div>
  );
};
